import * as Api from '@ViewModels';
import produce from 'immer';
import * as React from 'react';
import { useHistory } from 'react-router';
import { useEventLogging } from '../../../../models/Logging';
import { UsedAccountCreationImagesStorageKey } from '../../../../models/Storage';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import NEW_ACCOUNT_IMAGE_1 from '../../../../web/assets/new_account_image_1.png';
import NEW_ACCOUNT_IMAGE_2 from '../../../../web/assets/new_account_image_2.png';
import NEW_ACCOUNT_IMAGE_3 from '../../../../web/assets/new_account_image_3.png';
import NEW_ACCOUNT_IMAGE_4 from '../../../../web/assets/new_account_image_4.png';
import NEW_ACCOUNT_IMAGE_5 from '../../../../web/assets/new_account_image_5.png';
import NEW_ACCOUNT_IMAGE_6 from '../../../../web/assets/new_account_image_6.png';
import { ISelectBoxOption } from '../../../../web/components/SelectBox';
import { getBrokerDealerOptions } from '../getBrokerDealerOptions';
import { getSystemOfRecordOptions } from '../getSystemOfRecordOptions';
import { IndustryOptions } from './models';

export function useCreateAccount() {
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('CreateAccount');
	const history = useHistory();
	const [account, setAccount] = React.useState<Api.IAccount>({
		additionalInfo: {
			salesRepId: userSession.user.id,
			salesRepName: userSession.user.firstName + ' ' + userSession.user.lastName,
			source: 'Outbound',
		},
	});
	const [adminEmailAddress, setAdminEmailAddress] = React.useState<string>('');
	const [adminFirstName, setAdminFirstName] = React.useState<string>('');
	const [adminLastName, setAdminLastName] = React.useState<string>('');
	const [phoneNumber, setPhoneNumber] = React.useState<string>('');
	const [chargifyOfferLink, setChargifyOfferLink] = React.useState<string>('');
	const [showLoader, setShowLoader] = React.useState<boolean>(false);
	const [displayImage, setDisplayImage] = React.useState<string>();
	const [nextDisplayImage, setNextDisplayImage] = React.useState<string>();
	const [midMarket, setMidMarket] = React.useState<boolean>(false);
	const [hipaaEnabled, setHipaaEnabled] = React.useState<boolean>(false);

	const updateMidMarket = (e: React.ChangeEvent<HTMLInputElement>) => {
		setMidMarket(e.target.checked);
	};

	const updateHipaaEnabled = (e: React.ChangeEvent<HTMLInputElement>) => {
		setHipaaEnabled(e.target.checked);
	};

	const getIndustrySelection = () => {
		const industry = account?.additionalInfo?.industry;
		return IndustryOptions?.find(option => option.value === industry) || { title: 'Select One', value: '' };
	};

	const getBrokerDealerSelection = () => {
		const brokerDealer = account?.additionalInfo?.brokerDealer;
		const options = getBrokerDealerOptions(account?.additionalInfo?.industry);
		if (!options) {
			return { title: 'Select', value: '' };
		}
		if (!brokerDealer) {
			return { title: 'None', value: null };
		}
		return options.find(option => option.value === brokerDealer) || { title: 'Select', value: '' };
	};

	const getSystemOfRecordSelection = () => {
		const systemOfRecord = account?.additionalInfo?.systemOfRecord;
		const industry = account?.additionalInfo?.industry;
		return (
			getSystemOfRecordOptions(industry)?.find(x => x.value === systemOfRecord) || { title: 'Select One', value: '' }
		);
	};

	const updateAdminEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
		// Set the email domain based on the email address
		const nextAccount = produce(account, draftAccount => {
			draftAccount.emailDomain = e.target.value.substring(e.target.value.indexOf('@'));
		});
		setAccount(nextAccount);
		setAdminEmailAddress(e.target.value);
	};

	const updateCompanyName = (e: React.ChangeEvent<HTMLInputElement>) => {
		const nextAccount = produce(account, draftAccount => {
			draftAccount.companyName = e.target.value;
		});
		setAccount(nextAccount);
	};

	const updateFirstName = (e: React.ChangeEvent<HTMLInputElement>) => setAdminFirstName(e.target.value);
	const updateLastName = (e: React.ChangeEvent<HTMLInputElement>) => setAdminLastName(e.target.value);
	const updatePhoneNumber = (e: React.ChangeEvent<HTMLInputElement>) => setPhoneNumber(e.target.value);

	const onBrokerDealerChanged = (option: ISelectBoxOption<Api.BrokerDealer | null>): void => {
		const nextAccount = produce(account, draftAccount => {
			draftAccount.additionalInfo = account.additionalInfo || {};
			draftAccount.additionalInfo.brokerDealer = option?.value ?? null;
		});
		setAccount(nextAccount);
	};

	const onSystemOfRecordChanged = (option: ISelectBoxOption<Api.SystemOfRecord | string>): void => {
		if (option?.value) {
			const nextAccount = produce(account, draftAccount => {
				draftAccount.additionalInfo = account.additionalInfo || {};
				draftAccount.additionalInfo.systemOfRecord = option.value;
			});
			setAccount(nextAccount);
		}
	};

	const onIndustryChanged = (option: ISelectBoxOption<Api.Industry>) => {
		if (!!option && !!option.value) {
			const nextAccount = produce(account, draftAccount => {
				draftAccount.additionalInfo = account.additionalInfo || {};
				draftAccount.additionalInfo.industry = option.value;
				draftAccount.additionalInfo.brokerDealer = null;
			});
			setAccount(nextAccount);
			// Auto-enable HIPAA for Dental, Chiropractic, and Health industries
			const shouldEnableHipaa =
				option.value === Api.Industry.Dental ||
				option.value === Api.Industry.Chiropractic ||
				option.value === Api.Industry.Health;
			setHipaaEnabled(shouldEnableHipaa);
		}
	};

	const onAutocompleteItemSelectedSalesRep = (user: Api.IUser) => {
		const nextAccount = produce(account, draftAccount => {
			draftAccount.additionalInfo.salesRepId = user.id;
			draftAccount.additionalInfo.salesRepName = user.firstName + ' ' + user.lastName;
		});
		setAccount(nextAccount);
	};

	const getRandomImage = React.useCallback(
		(
			image: string | undefined,
			setImage: React.Dispatch<React.SetStateAction<string | undefined>>,
			prevImage?: string | undefined
		) => {
			const images = [
				NEW_ACCOUNT_IMAGE_1,
				NEW_ACCOUNT_IMAGE_2,
				NEW_ACCOUNT_IMAGE_3,
				NEW_ACCOUNT_IMAGE_4,
				NEW_ACCOUNT_IMAGE_5,
				NEW_ACCOUNT_IMAGE_6,
			];
			if (!image) {
				const usedAccountCreationImages = localStorage.getItem(UsedAccountCreationImagesStorageKey)?.split(',');
				let nextImages = images;
				nextImages = images.filter(img => !usedAccountCreationImages?.includes(img) && img !== prevImage);
				if (nextImages.length === 0) {
					nextImages = images;
					localStorage.setItem(UsedAccountCreationImagesStorageKey, '');
				}
				const nextImage = nextImages[Math.floor(Math.random() * nextImages.length)];
				setImage(nextImage);
				const updatedStorage = [localStorage.getItem(UsedAccountCreationImagesStorageKey)?.split(','), nextImage];
				localStorage.setItem(UsedAccountCreationImagesStorageKey, updatedStorage.join(','));
			}
		},
		[]
	);

	return {
		account,
		adminEmailAddress,
		adminFirstName,
		adminLastName,
		chargifyOfferLink,
		displayImage,
		errorMessages,
		getBrokerDealerSelection,
		getIndustrySelection,
		getSystemOfRecordSelection,
		getRandomImage,
		hipaaEnabled,
		history,
		logger,
		midMarket,
		nextDisplayImage,
		onBrokerDealerChanged,
		onAutocompleteItemSelectedSalesRep,
		onSystemOfRecordChanged,
		onIndustryChanged,
		phoneNumber,
		setAccount,
		setChargifyOfferLink,
		setDisplayImage,
		setNextDisplayImage,
		setShowLoader,
		showLoader,
		updateAdminEmail,
		updateCompanyName,
		updateFirstName,
		updateHipaaEnabled,
		updateLastName,
		updateMidMarket,
		updatePhoneNumber,
		userSession,
	};
}
